import { useSelector } from 'react-redux';
import { RootState } from '../app/store';
import UserRoles from '../enums/userRoles';
import { useMemo } from 'react';
import { UserAccess } from '../app/Slices/accessSlice';

export const useUserAccess = (): UserAccess => {
  const currentTeamInfo = useSelector(
    (state: RootState) => state.team.currentTeam
  );
  const personalAccountInfo = useSelector(
    (state: RootState) => state.team.personalAccount
  );

  // Compute access state from current data
  const personalAccount = personalAccountInfo.record || null;
  const team = currentTeamInfo.record;
  const currentTeamAccount = team?.current_user || null;
  const userRole = currentTeamAccount?.role;
  const isOwner = userRole === UserRoles.OWNER;
  const isAdmin = userRole === UserRoles.ADMIN;
  const isMember = userRole === UserRoles.MEMBER;
  const isTeamUser = !!currentTeamAccount?.license;
  const hasPersonalLicense = !isTeamUser;

  // Ensure currentTeamAccount has complete team data for ability checks
  // Check for team is functionally redundant but needed for TypeScript check
  const enhancedCurrentTeamAccount = useMemo(() => {
    return currentTeamAccount && team
      ? { ...currentTeamAccount, team }
      : currentTeamAccount;
  }, [currentTeamAccount, team]);

  const computedAccess: UserAccess = useMemo(
    () => ({
      currentAccount: enhancedCurrentTeamAccount || personalAccount,
      currentTeamAccount: enhancedCurrentTeamAccount,
      personalAccount,
      isOwner,
      isAdmin,
      isMember,
      hasOwnerAccess: isOwner,
      hasAdminAccess: isOwner || isAdmin,
      hasMemberAccess: isMember,
      isTeamUser,
      isPersonalUser: hasPersonalLicense,
    }),
    [
      enhancedCurrentTeamAccount,
      personalAccount,
      isOwner,
      isAdmin,
      isMember,
      isTeamUser,
      hasPersonalLicense,
    ]
  );

  return computedAccess;
};
